import React, { useState, useCallback, useRef, useMemo, useEffect, useContext, Fragment } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Menu, MenuItem, Divider } from "@mui/material";

import COMMON from "common";
import api from "services/api";
import pathnames from "routes/pathnames";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import { AxiosContext } from "contexts/with-interceptor-provider";
import { findCalendarByRegionCode, sortHolidays, filterHolidays, formatHolidayDate, getHolidayDuration, formatDuration, isMultiDayHoliday } from "common/holiday-calendar";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppCheckIcon from "components/icons/app-check-icon";
import AppArrowIcon from "components/icons/app-arrow-icon";
import AppChevronIcon from "components/icons/app-chevron-icon";
import AppCalendarDaysIcon from "components/icons/app-calendar-days-icon";
import AppCalendarPlusIcon from "components/icons/app-calendar-plus-icon";
import AppImportIcon from "components/icons/app-import-icon";
import AppHolidayModal from "components/pages/leave-management/app-holiday-modal";
import AppImportHolidaysModal from "components/pages/leave-management/app-import-holidays-modal";
import AppHolidayCalendarYearModal from "components/pages/leave-management/app-holiday-calendar-year-modal";
import AppConfirmDeleteHolidayCalendarModal from "components/pages/leave-management/app-confirm-delete-holiday-calendar-modal";
import addIcon from "assets/images/add-icon.svg";
import editIcon from "assets/images/edit-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import searchIcon from "assets/images/search-icon.svg";

const PageHolidayCalendar = () => {
	const { regionCode } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const context = useContext(AxiosContext);
	const holidayRef = useRef();
	const yearRef = useRef();
	const importRef = useRef();
	const confirmDeleteRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	/* setSearchParams is not reference stable, so it is held in a ref to keep it out of the
	   fetch callback's dependencies - otherwise the initial fetch effect would loop. */
	const memoSetSearchParams = useRef(setSearchParams);
	const [records, setRecords] = useState(null);
	const [keyword, setKeyword] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [rowAnchorEl, setRowAnchorEl] = useState(null);
	const [selectedHoliday, setSelectedHoliday] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const cancelRequest = useMemo(() => context.onHandleCancelRequest, [context.onHandleCancelRequest]);
	const calendar = useMemo(() => findCalendarByRegionCode(records, regionCode), [records, regionCode]);
	const years = useMemo(() => (calendar?.years || []).map((o) => o.year), [calendar]);
	/* The year lives in the query string so a refresh or a shared link lands on the same
	   year, falling back to the current year if the calendar has it, or the most recent
	   one otherwise. */
	const selectedYear = useMemo(() => {
		const param = parseInt(searchParams.get("year"));

		if (param) return param;

		const currentYear = new Date().getFullYear();

		if (years.includes(currentYear)) return currentYear;

		return years[0] || null;
	}, [searchParams, years]);
	const record = useMemo(() => (calendar?.years || []).find((o) => o.year === selectedYear) || null, [calendar, selectedYear]);
	const isMissingCalendar = useMemo(() => !!records && !calendar, [records, calendar]);
	const isMissingYear = useMemo(() => !!calendar && !record, [calendar, record]);
	/* Sorted order is the canonical order: it is what the table renders and what gets
	   written back, so a row's index is a safe handle for edit and delete even though
	   holidays have no id of their own. */
	const holidays = useMemo(() => sortHolidays(record?.holidays), [record]);
	//prettier-ignore
	const visibleHolidays = useMemo(() => {
		return filterHolidays(holidays.map((holiday, index) => ({ ...holiday, __index: index })), keyword);
	}, [holidays, keyword]);
	const isSearchEmpty = useMemo(() => !!keyword.trim() && !visibleHolidays.length && !!holidays.length, [keyword, visibleHolidays, holidays]);

	const onHandleGetList = useCallback(async (preferredYear) => {
		let response = null;

		try {
			response = await api.get.holidayCalendar.list();
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setRecords(response);

			if (preferredYear) memoSetSearchParams.current({ year: preferredYear.toString() }, { replace: true });
		}
	}, []);

	const onHandleBack = useCallback(() => {
		navigate(pathnames.leaveManagement.holidayCalendars);
	}, [navigate]);

	const onHandleSelectYear = useCallback((year) => {
		setAnchorEl(null);
		setKeyword("");
		memoSetSearchParams.current({ year: year.toString() }, { replace: true });
	}, []);

	const onHandleAddYear = useCallback(() => {
		setAnchorEl(null);
		yearRef.current.onHandleShow(calendar);
	}, [calendar]);

	const onHandleImport = useCallback(() => {
		importRef.current.onHandleShow({ calendar, record, records });
	}, [calendar, record, records]);

	/* A holiday is not a resource of its own - the whole year is updated in one call - so
	   the page owns persistence and the modals only hand back a value. */
	//prettier-ignore
	const onHandlePersistHolidays = useCallback(async (next, message) => {
		let response = null;

		setIsSaving(true);

		try {
			const payload = {
				id: record.id,
				title: record.title,
				region: record.region,
				regionCode: record.regionCode,
				year: record.year,
				holidays: next.map((o) => ({ title: o.title, startDate: o.startDate, endDate: o.endDate || null })),
			};

			await api.post.holidayCalendar.update(payload);

			response = true;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			setIsSaving(false);
		}

		if (response) {
			dispatch(promptAlertMessage({ message }));
			await onHandleGetList();
		}

		return response;
	}, [record, dispatch, onHandleGetList]);

	const onHandleAddHoliday = useCallback(() => {
		holidayRef.current.onHandleShow();
	}, []);

	const onHandleOpenRowMenu = useCallback((event, holiday) => {
		setRowAnchorEl(event.currentTarget);
		setSelectedHoliday(holiday);
	}, []);

	const onHandleCloseRowMenu = useCallback(() => {
		setRowAnchorEl(null);
	}, []);

	const onHandleEditHoliday = useCallback(() => {
		setRowAnchorEl(null);

		if (!selectedHoliday) return;

		holidayRef.current.onHandleShow({ ...selectedHoliday, index: selectedHoliday.__index });
	}, [selectedHoliday]);

	//prettier-ignore
	const onHandleSaveHoliday = useCallback(async (payload) => {
		const next = [...holidays];
		const value = { title: payload.title, startDate: payload.startDate, endDate: payload.endDate };
		const isEdit = Number.isInteger(payload.index);

		if (isEdit) next[payload.index] = value;
		else next.push(value);

		return onHandlePersistHolidays(next, isEdit ? "Holiday has been updated successfully" : "Holiday has been added successfully");
	}, [holidays, onHandlePersistHolidays]);

	//prettier-ignore
	const onHandleDeleteHoliday = useCallback(() => {
		setRowAnchorEl(null);

		if (!selectedHoliday) return;

		confirmDeleteRef.current.onHandleShow({
			title: "Delete Holiday",
			description: "This removes " + selectedHoliday.title + " from " + record.year + ".",
			confirmLabel: "Delete Holiday",
			onConfirm: () => onHandlePersistHolidays(holidays.filter((_, i) => i !== selectedHoliday.__index), "Holiday has been deleted successfully"),
		});
	}, [holidays, record, selectedHoliday, onHandlePersistHolidays]);

	//prettier-ignore
	const onHandleDeleteYear = useCallback(() => {
		const isLastYear = years.length === 1;

		confirmDeleteRef.current.onHandleShow({
			title: "Delete " + record.year,
			description: "This removes " + record.year + " and its holidays." + (isLastYear ? " The calendar will also be removed." : ""),
			confirmLabel: "Delete Year",
			onConfirm: async () => {
				try {
					await api.post.holidayCalendar.delete(record.id);
				} catch (error) {
					serveRequestErrors(error);
					return false;
				}

				dispatch(promptAlertMessage({ message: record.year + " has been deleted successfully" }));

				if (isLastYear) {
					onHandleBack();
					return true;
				}

				const nextYear = years.find((o) => o !== record.year);

				await onHandleGetList(nextYear);

				return true;
			},
		});
	}, [record, years, dispatch, onHandleGetList, onHandleBack]);

	const NameCell = useCallback(({ row }) => {
		return <AppTableCell left value={row.original.title || "-"} />;
	}, []);

	const DateCell = useCallback(({ row }) => {
		return <AppTableCell left value={formatHolidayDate(row.original.startDate) || "-"} />;
	}, []);

	const EndDateCell = useCallback(({ row }) => {
		return <AppTableCell left value={isMultiDayHoliday(row.original) ? formatHolidayDate(row.original.endDate) : "—"} />;
	}, []);

	const DurationCell = useCallback(({ row }) => {
		return <AppTableCell left value={formatDuration(getHolidayDuration(row.original))} />;
	}, []);

	const TypeCell = useCallback(({ row }) => {
		const multiDay = isMultiDayHoliday(row.original);

		return <span className={"holiday-calendar__badge" + (multiDay ? " holiday-calendar__badge--blue" : "")}>{multiDay ? "Multi-Day" : "Single Day"}</span>;
	}, []);

	const MenuCell = useCallback(({ row }) => {
		return <AppTableMoreIcon disabled={isSaving} onClick={(event) => onHandleOpenRowMenu(event, row.original)} />;
	}, [isSaving, onHandleOpenRowMenu]);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Holiday Name",
			accessor: "title",
			disableSortBy: true,
			Cell: NameCell,
		},
		{
			Header: "Date",
			accessor: "startDate",
			disableSortBy: true,
			Cell: DateCell,
		},
		{
			Header: "End Date",
			accessor: "endDate",
			disableSortBy: true,
			Cell: EndDateCell,
		},
		{
			Header: "Duration",
			accessor: "duration",
			disableSortBy: true,
			Cell: DurationCell,
		},
		{
			Header: "Type",
			accessor: "type",
			disableSortBy: true,
			Cell: TypeCell,
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuCell,
		},
	], [NameCell, DateCell, EndDateCell, DurationCell, TypeCell, MenuCell]);

	/* Holidays are never paginated - a region's whole year fits on one page - so AppTable
	   is handed a single page holding every filtered holiday and no onHandlePagination,
	   which keeps its footer from rendering. */
	const pages = useMemo(() => ({ page: 1, size: visibleHolidays.length || 1, total: 1, offset: 0, totalElements: visibleHolidays.length, prev: false, next: false, items: visibleHolidays }), [visibleHolidays]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.HOLIDAY_CALENDAR.LIST);
		};
	}, [cancelRequest]);

	return (
		<div className="page-holiday-calendar">
			<div className="holiday-calendar">

				<div className="holiday-calendar__header">
					<div className="holiday-calendar__headline">
						<h1 className="holiday-calendar__title">{calendar?.title || "-"}</h1>

						<p className="holiday-calendar__subtitle">{calendar?.region || calendar?.regionCode}</p>
					</div>

					<div className="holiday-calendar__actions">
						{!isMissingYear && !!holidays.length && (
							<Fragment>
								<AppButton type="button" outline label="Import" icon={<AppImportIcon color="#0245a9" />} disabled={isSaving} onClick={onHandleImport} />
								<AppButton type="button" label="Add Holiday" icon={addIcon} disabled={isSaving} onClick={onHandleAddHoliday} />
							</Fragment>
						)}
					</div>
				</div>

				<div className="holiday-calendar__controls">
					<div className="holiday-calendar__controls-left">
						{!isMissingYear && (
							/* prettier-ignore */
							<AppInput type="text" name="keyword" icon={searchIcon} placeholder="Search holidays" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
						)}
					</div>

					<div className="holiday-calendar__controls-right">
						<button type="button" className="holiday-calendar__year-select" onClick={(event) => setAnchorEl(event.currentTarget)}>
							<AppCalendarDaysIcon className="holiday-calendar__year-icon" color="#0245a9" />
							<span className="holiday-calendar__year-value">{selectedYear || "-"}</span>
							<span className="holiday-calendar__year-chevron">
								<AppChevronIcon color="#0245a9" />
							</span>
						</button>

						{!isMissingYear && (
							<button type="button" className="holiday-calendar__delete-year" disabled={!record} onClick={onHandleDeleteYear} aria-label="Delete year">
								<img src={removeIcon} alt="delete" />
							</button>
						)}

						<Menu classes={{ root: "app-table-menu holiday-calendar__year-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
							{years.map((year) => (
								<MenuItem key={year} classes={{ root: "holiday-calendar__year-option" }} onClick={() => onHandleSelectYear(year)}>
									{year}
									<span className="holiday-calendar__year-check">{year === selectedYear && <AppCheckIcon color="#0245a9" />}</span>
								</MenuItem>
							))}

							<Divider />

							<MenuItem classes={{ root: "holiday-calendar__year-add" }} onClick={onHandleAddYear}>
								Add New Year
								<AppCalendarPlusIcon className="holiday-calendar__year-plus" color="#0245a9" />
							</MenuItem>
						</Menu>
					</div>
				</div>

				{isMissingYear && <AppEmptyState title={"No calendar for " + selectedYear} description="This region has no holidays recorded for that year yet. Create the year to start adding them." buttons={[{ label: "Create Year", icon: addIcon, onClick: onHandleAddYear }]} />}

				{!isMissingYear && !holidays.length && (
					<div className="holiday-calendar__empty">
						<div className="holiday-calendar__empty-icon">
							<AppCalendarDaysIcon color="#c0c0c0" />
						</div>

						<p className="holiday-calendar__empty-title">No holidays configured for {selectedYear}</p>
						<p className="holiday-calendar__empty-description">Add holidays one at a time, or import them from another calendar or year.</p>

						<div className="holiday-calendar__empty-buttons">
							<AppButton type="button" label="Add Holiday" icon={addIcon} disabled={isSaving} onClick={onHandleAddHoliday} />
							<AppButton type="button" outline label="Import" icon={<AppImportIcon color="#0245a9" />} disabled={isSaving} onClick={onHandleImport} />
						</div>
					</div>
				)}

				{!isMissingYear && !!holidays.length && (
					<Fragment>
						{!!visibleHolidays.length && <AppTable columns={columns} pages={pages} />}

						{isSearchEmpty && <p className="holiday-calendar__no-results">No holidays matching "{keyword.trim()}"</p>}
					</Fragment>
				)}

				<Menu classes={{ root: "app-table-menu" }} anchorEl={rowAnchorEl} open={!!rowAnchorEl} onClose={onHandleCloseRowMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
					<MenuItem onClick={onHandleEditHoliday}>
						<img className="app-table-menu__icon" src={editIcon} alt="edit" />
						Edit
					</MenuItem>

					<MenuItem onClick={onHandleDeleteHoliday}>
						<img className="app-table-menu__icon" src={removeIcon} alt="delete" />
						Delete
					</MenuItem>
				</Menu>
			</div>

			<AppHolidayModal ref={holidayRef} onHandleSave={onHandleSaveHoliday} />

			<AppHolidayCalendarYearModal ref={yearRef} onHandleGetList={onHandleGetList} />

			<AppImportHolidaysModal ref={importRef} onHandleGetList={onHandleGetList} />

			<AppConfirmDeleteHolidayCalendarModal ref={confirmDeleteRef} />
		</div>
	);
};

export default PageHolidayCalendar;
